import {gql} from '@apollo/client';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import {connect} from 'react-redux';

import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import requestFetchValidationExtras from 'bioentities/requestFetchValidationExtras';
import * as SourceConstants from 'constants/SourceConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getPrimaryRegistryIdentifier} from 'registry/RegistryIdentifierUtil';
import getValidationErrorText from 'schemas/getValidationErrorText';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import {getIconForBioentity} from 'ui-library/chips/getIconForBioentity';
import APIUtil from 'util/APIUtil';

import {BioentityChipSummaryGraphQL} from '../../../graphql-fragments/registry/registry-items/__generated/BioentityChipSummaryGraphQL';
import {BioentityChipQuery, BioentityChipQueryVariables} from './__generated/BioentityChipQuery';
import SmartChip from './SmartChip';
import {BioentityAdapterOutput} from './snapshotAdapters';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

export const BIOENTITY_CHIP_QUERY = gql`
  query BioentityChipQuery($id: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    bioentity(id: $id) {
      ...BioentityChipOrInaccessibleGraphQL
    }
  }
  ${getFragmentWrapper('BioentityChipOrInaccessible').fragment}
`;

export async function doBioentityChipQuery(bioentityId: string): Promise<void> {
  await doQuerySafe<BioentityChipQuery, BioentityChipQueryVariables>(
    {query: BIOENTITY_CHIP_QUERY},
    {variables: {id: bioentityId}, fetchPolicy: 'network-only'}
  );
}

export interface BioentityChipProps extends SharedSmartChipProps<SmartChipType.BIOENTITY> {
  validationExtras: ValidationExtras | null;
  versionId?: number;
  shouldEllipsize?: boolean;
}

function isReadonly(bioentity?: BioentityChipSummaryGraphQL): boolean {
  if (!bioentity) {
    return false;
  }

  // Disable clicking through to molecule entity if chemistry is not enabled
  if (APIUtil.isMolecule(bioentity.id) && !BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
    return true;
  }

  const isRegistered =
    bioentity.folder != null &&
    bioentity.folder.source != null &&
    bioentity.folder.source.sourceType === SourceConstants.REGISTRY_TYPE;

  return !isFeatureEnabled({
    feature: StandardFeature.OPEN,
    item: bioentity,
    itemState: {isRegistered, isBiologicsOwned: bioentity.isBiologicsOwned},
  });
}

/**
 * Returns the secondary text (e.g. registry ID) for a bioentity chip.
 * During export, SmartChipSnapshotContext provides a pre-computed value because the data
 * needed for live derivation (bioentityAliasInfos, folder.source) isn't available.
 */
export function getBioentityChipDefaultSecondaryText(bioentity: BioentityChipSummaryGraphQL): string | null {
  if (!bioentity?.tagSchema?.includeRegistryIdInChips) {
    return null;
  }
  const snapshotSecondaryText = (bioentity as BioentityChipSummaryGraphQL & Partial<BioentityAdapterOutput>)
    .secondaryText;
  return snapshotSecondaryText ?? getPrimaryRegistryIdentifier(bioentity);
}

export function BioentityChip(props: BioentityChipProps): JSX.Element {
  const {validationExtras, versionId} = props;
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const smartChipResult = useSmartChipQuery<SmartChipType.BIOENTITY>({
    id,
    onClick,
    getUrlFn: ({editUrl}) => editUrl,
  });

  const {item: bioentity, handleClickLink, isInaccessible, itemUrl: url} = smartChipResult;

  const icon = bioentity ? getIconForBioentity(bioentity) : null;

  const tooltipTitleOverride =
    props.tooltipTitleOverride ??
    (bioentity ? getValidationErrorText(bioentity.schemaValidationStateJson, validationExtras) : null);

  const getSecondaryText = props.getSecondaryText ?? getBioentityChipDefaultSecondaryText;

  return (
    <SmartChip
      {...sharedSmartChipProps}
      className={classNames(props.className, {
        [css(styles.singleLine)]: props.shouldEllipsize,
      })}
      icon={icon}
      isInaccessible={isInaccessible}
      item={bioentity}
      isReadonly={isReadonly(bioentity)}
      negative={bioentity ? isFailed(bioentity.schemaValidationStateJson) : null}
      onClick={handleClickLink}
      requestFetch={(id) => {
        requestFetchValidationExtras(id);
      }}
      tooltipTitleOverride={tooltipTitleOverride}
      popoverContentOverride={
        <BioentityPopoverContent
          id={id}
          versionId={versionId}
          validationExtras={props.validationExtras}
          // Because the float-ui library does not do a good job handling dropdowns in popovers, but it DOES
          // support nested popovers well, we want to render the summary chips as popovers (rather than
          // as dropdowns).
          showSummaryChipsAsPopover={true}
        />
      }
      url={url}
      getSecondaryText={getSecondaryText}
    />
  );
}

const mapStoreStateToProps = (
  state: BenchlingState,
  {id}: {id?: string | null}
): Pick<BioentityChipProps, 'validationExtras'> =>
  // The caller should specify either a non-null id, or explicitly indicate that they don't have the
  // id available and pass isInaccessible=true, to render a (No access) chip. However, passing in a
  // non-null id can still result in a (No access) chip, if the id points to a file that the user
  // lost permissions.
  ({
    validationExtras: id ? state.models.validationExtrasByBioentityId[id] : null,
  });
export default connect(mapStoreStateToProps)(BioentityChip);

const styles = StyleSheet.create({
  singleLine: {
    whiteSpace: 'nowrap',
  },
});
